// Performance optimization utilities for Perxify website
import React from 'react';

// Image optimization
export const getOptimizedImageUrl = (src: string, width: number = 0, quality: number = 75): string => {
  // If it's an external URL or SVG, return as is
  if (src.startsWith('http') || src.endsWith('.svg')) {
    return src;
  }
  
  // For local images, add width and quality parameters
  if (width > 0) {
    return `${src}?w=${width}&q=${quality}`;
  }
  
  return `${src}?q=${quality}`;
};

// Lazy loading utilities
export const lazyLoadImages = (): void => {
  if (typeof window !== 'undefined') {
    // Use Intersection Observer to lazy load images
    const images = document.querySelectorAll('img[data-src]');
    
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const dataSrc = img.getAttribute('data-src');
          
          if (dataSrc) {
            img.src = dataSrc;
            img.removeAttribute('data-src');
          }
          
          imageObserver.unobserve(img);
        }
      });
    });
    
    images.forEach(img => {
      imageObserver.observe(img);
    });
  }
};

// Script loading optimization
export const loadScriptAsync = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    
    document.head.appendChild(script);
  });
};

// CSS optimization
export const loadStyleAsync = (href: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
    
    document.head.appendChild(link);
  });
};

// Font optimization
export const optimizeFontLoading = (): void => {
  if (typeof window !== 'undefined') {
    // Add font display swap to improve perceived performance
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: 'Montserrat';
        font-display: swap;
      }
    `;
    document.head.appendChild(style);
  }
};

// Component lazy loading
export const lazyLoadComponent = <T extends React.ComponentType<Record<string, unknown>>>(
  importFunc: () => Promise<{ default: T }>
) => {
  return React.lazy(importFunc);
};
